import { makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components';
import { ChevronDown16Regular } from '@fluentui/react-icons';
import { useT } from '@powerportalspro/react';
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';

// A Demos page's frame: the demo, and beside it — below it, on a narrow screen — a panel of what to
// try in it, which folds away behind its toggle to a slim strip at the edge, or to the toggle alone
// below the demo. Open at first; the visitor's choice is kept in the browser for every demo page, on
// both stacks (the Blazor DemoShowcaseLayout is its twin, under the same storage key).

const STRINGS_BASE = 'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Common.DemoShowcaseLayout';

// Shared with the Blazor DemoShowcaseLayout, so the choice holds on either stack.
const STORAGE_KEY = 'ppp-demo-tips';

const NARROW = '@media (max-width: 1100px)';

const useStyles = makeStyles({
  root: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, var(--ppp-demo-showcase-share, 3fr)) minmax(0, 1fr)',
    gap: tokens.spacingHorizontalXXL,
    alignItems: 'start',
    [NARROW]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: tokens.spacingVerticalXXL,
    },
  },
  // Folded, the panel is a slim strip at the edge and the demo takes the width.
  rootCollapsed: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: tokens.spacingHorizontalL,
    [NARROW]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: tokens.spacingVerticalXXL,
    },
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalM}`,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
  },
  panelCollapsed: {
    alignSelf: 'stretch',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXXS}`,
    [NARROW]: {
      alignSelf: 'auto',
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    },
  },
  // The panel's title, which opens and folds it.
  toggle: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    margin: 0,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    border: 'none',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase300,
    textAlign: 'start',
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
    ':active': { backgroundColor: tokens.colorSubtleBackgroundPressed },
    ':focus-visible': {
      outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: tokens.strokeWidthThin,
    },
  },
  // Folded at the edge: the title runs down the strip.
  toggleCollapsed: {
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXS}`,
    [NARROW]: {
      flexDirection: 'row',
      padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    },
  },
  titleCollapsed: {
    writingMode: 'vertical-rl',
    [NARROW]: { writingMode: 'horizontal-tb' },
  },
  // One chevron, turned to point where the panel goes: toward the edge to fold, away to open.
  chevron: {
    display: 'inline-flex',
    flexShrink: 0,
    transform: 'rotate(-90deg)',
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    [NARROW]: { transform: 'rotate(180deg)' },
    '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0.01ms' },
  },
  chevronCollapsed: {
    transform: 'rotate(90deg)',
    [NARROW]: { transform: 'rotate(0deg)' },
  },
  // A heading among the tips, under the panel's own title.
  heading: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  tips: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    padding: `0 ${tokens.spacingHorizontalS}`,
    '&[hidden]': { display: 'none' },
  },
});

function readOpen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'closed';
  } catch {
    // No storage, or none allowed: the panel stays open.
    return true;
  }
}

function writeOpen(open: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, open ? 'open' : 'closed');
  } catch {
    // Kept for this page alone.
  }
}

/** A heading among the tips, under the panel's own title. */
export function DemoShowcaseHeading({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <div className={mergeClasses('ppp-demo-showcase-heading', styles.heading)}>{children}</div>;
}

export interface DemoShowcaseLayoutProps {
  /** The panel's title, shown on its toggle: the page's "Try it". */
  title: string;
  /** What to try in the demo, and anything else the panel says. */
  tips: ReactNode;
  /** How many times the panel's width the demo takes, side by side. Defaults to 3. */
  demoShare?: number;
  /** The demo. */
  children: ReactNode;
}

export function DemoShowcaseLayout({ title, tips, demoShare = 3, children }: DemoShowcaseLayoutProps) {
  const styles = useStyles();
  const t = useT();
  const tipsId = useId();

  // Open at first, as the Blazor frame draws before it reads the choice; then the choice.
  const [open, setOpen] = useState(true);
  useEffect(() => setOpen(readOpen()), []);

  const toggle = () => {
    setOpen(!open);
    writeOpen(!open);
  };

  return (
    <div
      className={mergeClasses('ppp-demo-showcase', !open && 'ppp-demo-showcase--collapsed', styles.root, !open && styles.rootCollapsed)}
      style={{ '--ppp-demo-showcase-share': `${demoShare}fr` } as CSSProperties}
    >
      <div className="ppp-demo-showcase-demo">{children}</div>

      <aside className={mergeClasses('ppp-demo-showcase-panel', styles.panel, !open && styles.panelCollapsed)}>
        <Tooltip content={t(`${STRINGS_BASE}.${open ? 'hide-tips' : 'show-tips'}`)} relationship="description">
          <button
            type="button"
            className={mergeClasses('ppp-demo-showcase-toggle', styles.toggle, !open && styles.toggleCollapsed)}
            aria-expanded={open}
            aria-controls={tipsId}
            onClick={toggle}
          >
            <span className={mergeClasses('ppp-demo-showcase-chevron', styles.chevron, !open && styles.chevronCollapsed)} aria-hidden>
              <ChevronDown16Regular />
            </span>
            <span className={mergeClasses('ppp-demo-showcase-title', !open && styles.titleCollapsed)}>{title}</span>
          </button>
        </Tooltip>
        <div id={tipsId} className={mergeClasses('ppp-demo-showcase-tips', styles.tips)} hidden={!open}>
          {tips}
        </div>
      </aside>
    </div>
  );
}
